import React from "react";

import { Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Toaster } from "react-hot-toast";
import "./styles/notifications.css";
import { MyTeamPitch } from "./pages/TeamPage";
import { MyTeamList } from "./pages/TeamPageList";
import { PlayerPage } from "./pages/PlayerPage";
const PublicTeamView = React.lazy(() => import("./pages/PublicTeamView"));
import { LeagueTablePage } from "./pages/LeagueTablePage";
import { LeagueFixturesPage } from "./pages/LeagueFixturesPage";
import { TeamOfTheWeekPage } from "./pages/TeamOfTheWeekPage";
import { SeasonAwardsPage } from "./pages/SeasonAwardsPage";
import { TransfersPage } from "./pages/TransfersPage";
import { TransfersSignedPage } from "./pages/TransfersSignedPage";
import { TransfersOngoingPage } from "./pages/TransfersOngoingPage";
import { GuestLeaveWarning } from "./components/auth/GuestLeaveWarning";

import { MatchLineups } from "./pages/MatchLineups";
import MatchPage from "./pages/MatchPage";
import { MatchSummary } from "./pages/MatchSummary";
import { MatchesPage } from "./pages/MatchesPage";

import { LoginPage } from "./pages/LoginPage";
import { AuthCallback } from "./pages/AuthCallback";
import { RegisterPage } from "./pages/RegisterPage";
import { PasswordResetRequest } from "./pages/PasswordResetRequest";
import { PasswordReset } from "./pages/PasswordReset";
import { LeagueInvitePage } from "./pages/LeagueInvitePage";
import { LeagueNotFoundGuard } from "./components/league/LeagueNotFoundGuard";
import { LandingAbout, LandingContacts, LandingHome, LandingPolicy, LandingTerms } from "./pages/PublicPages";
import { QuickPlayPage } from "./pages/QuickPlayPage";
import { PlayWithFriendsPage } from "./pages/PlayWithFriendsPage";

import { SettingsPersonal } from "./pages/SettingsPersonal";
import { SettingsNotifications } from "./pages/SettingsNotifications";
// import { SettingsPrivacy } from "./pages/SettingsPrivacy";

import { TeamSettings } from "./pages/TeamSettings";
import { LeagueSettings } from "./pages/LeagueSettings";

import { Onboarding } from "./pages/Onboarding";
import { MorePage } from "./pages/MorePage";
import { CreateLeague } from "./pages/CreateLeague";
import { LeagueSwitchPage } from "./pages/LeagueSwitchPage";
import { CreateTeamPage } from "./pages/CreateTeamPage";
import NotificationsInboxPage from "./pages/NotificationsInboxPage";

import { NotFoundPage } from "./pages/404";
import { Layout } from "./pages/Layout";
import { ActiveLeagueProvider } from './context/ActiveLeagueContext.jsx';
import { RequireAuth } from "./components/routing/RequireAuth.jsx";
import { GuestTransferGate } from "./components/routing/GuestTransferGate.jsx";
import { resolveDefaultPostLoginPath } from "./components/routing/loginRedirect.js";
import { useActiveLeague } from "./hooks/useActiveLeague";
import { useAuth } from "./hooks/useAuth";
import { useGetUserLeagues } from "./api/leaguesApi";
import { registerPushToken } from "./api/push";
import { getPushTokenIfPermissionGranted, subscribeToForegroundPushMessages } from "./lib/firebase";

const FCM_TOKEN_STORAGE_KEY = 'fcmToken';

function showForegroundPushNotification({ title, body, targetUrl }) {
  if (typeof window === 'undefined') return false;
  if (!('Notification' in window) || Notification.permission !== 'granted') return false;

  try {
    const browserNotification = new Notification(title, {
      body,
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      data: { url: targetUrl || '/' }
    });

    browserNotification.onclick = () => {
      window.focus();
      if (targetUrl) {
        window.location.assign(targetUrl);
      }
      browserNotification.close();
    };
    return true;
  } catch (err) {
    console.warn('Foreground browser notification failed:', err);
    return false;
  }
}

// Redirects un-scoped paths (missing league slug) to slug-prefixed equivalent when activeLeague is present
function UnscopedRedirect() {
  const { user } = useAuth();
  const location = useLocation();
  const { activeLeague } = useActiveLeague();
  // Fetch user leagues to decide redirect when exactly one league exists
  const { data: userLeagues, isLoading: leaguesLoading } = useGetUserLeagues(!!user);

  if (leaguesLoading) {
    // Wait until leagues list is loaded to avoid flashing the switch page
    return null; // Can be replaced with a spinner
  }

  if (!activeLeague?.slug) {
    // No active league has been chosen yet
    const leagueArray = Array.isArray(userLeagues) && userLeagues.length > 0
      ? userLeagues
      : (Array.isArray(user?.leagues) ? user.leagues : []);

    if (!user) {
      const redirectTarget = encodeURIComponent(`${location.pathname}${location.search || ''}${location.hash || ''}`);
      return <Navigate to={`/login?redirect=${redirectTarget}`} replace />;
    }

    if (user) {
      const leagueCount = leagueArray.length;
      if (leagueCount === 0) {
        // User hasn't joined any league yet → onboarding flow
        return <Navigate to="/onboarding" replace />;
      }
      if (leagueCount === 1) {
        // Auto-redirect straight into their only league
        const onlyLeague = leagueArray[0];
        const targetPath = `/${onlyLeague.slug}${location.pathname === '/' ? '/table' : location.pathname}`;
        return <Navigate to={targetPath} replace />;
      }
    }
    // Two or more leagues – let them choose
    return <Navigate to="/switch-league" replace />;
  }
  const slugPrefix = `/${activeLeague.slug}`;
  // If already scoped, show 404
  if (location.pathname.startsWith(slugPrefix)) return <NotFoundPage />;
  return <Navigate to={`${slugPrefix}${location.pathname}`} replace />;
}

function App() {
  const { user, loading } = useAuth();
  const { data: userLeagues, isLoading: leaguesLoading } = useGetUserLeagues(!!user);

  React.useEffect(() => {
    if (!user) return undefined;

    let cancelled = false;

    async function syncExistingPushToken() {
      try {
        const token = await getPushTokenIfPermissionGranted();
        if (!token || cancelled) return;

        await registerPushToken(token);
        if (cancelled) return;

        try {
          localStorage.setItem(FCM_TOKEN_STORAGE_KEY, token);
        } catch (_) {
          // Non-fatal: server-side token sync is the important part.
        }
      } catch (err) {
        console.warn('FCM token sync failed:', err);
      }
    }

    syncExistingPushToken();

    return () => {
      cancelled = true;
    };
  }, [user]);

  React.useEffect(() => {
    if (!user) return undefined;

    return subscribeToForegroundPushMessages((payload) => {
      const title = payload?.notification?.title || 'Nova';
      const body = payload?.notification?.body || 'You have a new notification.';
      const targetUrl = payload?.data?.url;

      if (showForegroundPushNotification({ title, body, targetUrl })) {
        return;
      }

      toast.info(`${title}: ${body}`, {
        onClick: () => {
          if (targetUrl) {
            window.location.assign(targetUrl);
          }
        }
      });
    });
  }, [user]);

  // Updated renderIndexElement for the root path "/"
  const renderRootElement = () => {
    if (loading || leaguesLoading) {
      return <div>Loading...</div>;
    }
    if (user) {
      return <Navigate to={resolveDefaultPostLoginPath(userLeagues, user)} replace />;
    }
    return <LandingHome />;
  };

  return (
    <>
      <GuestLeaveWarning />
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{ zIndex: 9999 }}
      />
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            zIndex: 9999,
          },
        }}
      />
      <Routes>
        {/* Authentication Routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/password-reset-request" element={<PasswordResetRequest />} />
        <Route path="/password-reset" element={<PasswordReset />} />
        <Route path="/invite/:token" element={<LeagueInvitePage />} />
        <Route path="/play" element={<QuickPlayPage />} />
        <Route path="/play-with-friends" element={<PlayWithFriendsPage />} />
        <Route path="/about" element={<LandingAbout />} />
        <Route path="/contacts" element={<LandingContacts />} />
        <Route path="/contact" element={<Navigate to="/contacts" replace />} />
        <Route path="/policy" element={<LandingPolicy />} />
        <Route path="/privacy" element={<Navigate to="/policy" replace />} />
        <Route path="/terms" element={<LandingTerms />} />
        <Route path="/terms-of-service" element={<Navigate to="/terms" replace />} />

        {/* Root path handler - decides where to go if user hits "/" */}
        <Route path="/" element={renderRootElement()} />

        <Route element={<RequireAuth />}>
          {/* Onboarding & Pre-League Selection Global Routes */}
          <Route path="/create-league" element={<CreateLeague />} />
          <Route path="/create-team" element={(
            <ActiveLeagueProvider>
              <CreateTeamPage />
            </ActiveLeagueProvider>
          )} />

          {/* Wrap pages that need Layout but are not league-specific */}
          <Route element={(
            <ActiveLeagueProvider>
              <Layout />
            </ActiveLeagueProvider>
          )}>
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/more" element={<MorePage />} />
            <Route path="/switch-league" element={<LeagueSwitchPage />} />
          </Route>

          {/* League Specific Routes - Legacy support for old prefix "/league/:slug/*" */}
          <Route path="/league/:slug/*" element={(
              <ActiveLeagueProvider>
                <LeagueNotFoundGuard>
                  <Layout />
                </LeagueNotFoundGuard>
              </ActiveLeagueProvider>
            )}>
            <Route index element={<Navigate to="table" replace />} />
            <Route path="table" element={<LeagueTablePage />} />
            <Route path="fixtures" element={<LeagueFixturesPage />} />
            <Route path="totw" element={<TeamOfTheWeekPage />} />
            <Route path="season-awards" element={<SeasonAwardsPage />} />
            <Route path="matches" element={<MatchesPage />} />
            <Route path="match/:id/lineups" element={<MatchLineups />} />
            <Route path="match/:id/match" element={<MatchPage />} />
            <Route path="match/:id/summary/:teamId" element={<MatchSummary />} />
            <Route element={<GuestTransferGate><Outlet /></GuestTransferGate>}>
              <Route path="transfers-players" element={<TransfersPage />} />
              <Route path="transfers-ongoing" element={<TransfersOngoingPage />} />
              <Route path="transfers-signed" element={<TransfersSignedPage />} />
            </Route>
            <Route path="team-pitch/:id" element={<MyTeamPitch />} />
            <Route path="team-list/:id" element={<MyTeamList />} />
            <Route path="team/:id" element={<React.Suspense fallback={<div>Loading...</div>}><PublicTeamView /></React.Suspense>} />
            <Route path="player/:wyId" element={<PlayerPage />} />
            <Route path="team-settings/:id" element={<TeamSettings />} />
            <Route path="league-settings" element={<LeagueSettings />} />
            <Route path="settings-personal" element={<SettingsPersonal />} />
            <Route path="settings-notifications" element={<SettingsNotifications />} />
          </Route>

          {/* League Specific Routes - All under /:slug and use Layout */}
          <Route path="/:slug/*" element={(
              <ActiveLeagueProvider>
                <LeagueNotFoundGuard>
                  <Layout />
                </LeagueNotFoundGuard>
              </ActiveLeagueProvider>
            )}>
            <Route index element={<Navigate to="table" replace />} /> {/* Default to league table */}
            <Route path="table" element={<LeagueTablePage />} />
            <Route path="fixtures" element={<LeagueFixturesPage />} />
            <Route path="totw" element={<TeamOfTheWeekPage />} />
            <Route path="season-awards" element={<SeasonAwardsPage />} />
            <Route path="matches" element={<MatchesPage />} />
            <Route path="match/:id/lineups" element={<MatchLineups />} />
            <Route path="match/:id/match" element={<MatchPage />} />
            <Route path="match/:id/summary/:teamId" element={<MatchSummary />} />
            <Route element={<GuestTransferGate><Outlet /></GuestTransferGate>}>
              <Route path="transfers-players" element={<TransfersPage />} />
              <Route path="transfers-ongoing" element={<TransfersOngoingPage />} />
              <Route path="transfers-signed" element={<TransfersSignedPage />} />
            </Route>
            <Route path="transfers-shortlist" element={<Navigate to="../transfers-players" replace />} />
            <Route path="team-pitch/:id" element={<MyTeamPitch />} />
            <Route path="team-list/:id" element={<MyTeamList />} />
            <Route path="team/:id" element={<React.Suspense fallback={<div>Loading...</div>}><PublicTeamView /></React.Suspense>} />
            <Route path="player/:wyId" element={<PlayerPage />} />
            <Route path="team-settings/:id" element={<TeamSettings />} />
            <Route path="league-settings" element={<LeagueSettings />} />
            <Route path="settings-personal" element={<SettingsPersonal />} />
            <Route path="settings-notifications" element={<SettingsNotifications />} />
            <Route path="notifications" element={<NotificationsInboxPage />} />
            {/* Add any other league-specific routes that were previously direct children of the old Layout */}
          </Route>
        </Route>

        {/* Fallback: attempt to scope into active league when slug missing */}
        <Route path="*" element={<UnscopedRedirect />} />
      </Routes>
    </>
  );
}

export default App;
